// The search across the top (BL-134, pages/ux-full app.js's .search): one box on every page, `/` to
// reach it, its answers grouped (customers, jobs, invoices, estimates, phone numbers) under it. The
// arrows move through them, Enter opens one (the first, if none is picked), Esc closes the list and
// clears the box. It asks search.all once the typing pauses, as the person: the server answers only
// what they may open. A customer or an invoice opens at its own link; a job or an estimate on its job's
// sheet, over the page.
// Until the search is worked out (Amir, 26 Oct), the top shows it as a magnifying glass ("Search"): a
// press on it, or `/`, opens the box there and puts the typing in it; leaving it empty, or Esc on an
// empty box, closes it back to the glass.
import { useEffect, useId, useRef, useState } from 'react';
import { client } from '../api.ts';
import { openJob } from '../job/job-page.ts';
import {
  allHits,
  hitGroups,
  searchInput,
  SEARCH_WAIT_MS,
  step,
  takesSlash,
  type Found,
  type Hit,
  type HitGroup,
} from './search-model.ts';

type State =
  | { kind: 'idle' }
  | { kind: 'asking' }
  | { kind: 'found'; query: string; groups: HitGroup[] }
  | { kind: 'failed' };

export function HeaderSearch() {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [at, setAt] = useState(-1);
  const asked = useRef(0);
  // the box shows while it's in use; otherwise the glass
  const [shown, setShown] = useState(false);
  const glass = useRef<HTMLButtonElement>(null);
  const focusBox = useRef(false);
  useEffect(() => {
    if (!shown || !focusBox.current) return;
    focusBox.current = false;
    input.current?.focus();
    input.current?.select();
  }, [shown]);
  const openBox = useRef(() => {});
  openBox.current = () => {
    focusBox.current = true;
    setShown(true);
    if (shown) {
      input.current?.focus();
      input.current?.select();
    }
  };

  // `/` anywhere but in a field goes to the box
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const keys = { key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey };
      if (!takesSlash({ ...keys, target })) return;
      e.preventDefault();
      openBox.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // once the typing pauses, ask; an answer to an older question is dropped
  useEffect(() => {
    const ask = searchInput(text);
    setAt(-1);
    if (!ask) {
      asked.current++;
      setState({ kind: 'idle' });
      return;
    }
    const t = setTimeout(() => {
      const n = ++asked.current;
      setState({ kind: 'asking' });
      client
        .call<Found>('search.all', ask)
        .then((f) => {
          if (n === asked.current) setState({ kind: 'found', query: ask.query, groups: hitGroups(f) });
        })
        .catch(() => {
          if (n === asked.current) setState({ kind: 'failed' });
        });
    }, SEARCH_WAIT_MS);
    return () => clearTimeout(t);
  }, [text]);

  const groups = state.kind === 'found' ? state.groups : [];
  const hits = allHits(groups);
  const listed = open && state.kind !== 'idle';
  const optionId = (i: number) => `${id}-hit-${i}`;

  const close = () => {
    setOpen(false);
    setAt(-1);
  };
  const go = (h: Hit) => {
    setText('');
    close();
    input.current?.blur();
    if ('job' in h.opens) openJob(h.opens.job);
    else location.hash = h.opens.href;
  };

  let n = -1; // each option's place in the arrows' order, as the groups draw them
  if (!shown)
    return (
      <button
        ref={glass}
        type="button"
        className="bp-find-btn"
        aria-label="Search"
        aria-keyshortcuts="/"
        title="Search (/)"
        onClick={() => openBox.current()}
      >
        <svg className="bp-find-ic" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="10.5" cy="10.5" r="6" />
          <path d="M15 15l5 5" />
        </svg>
      </button>
    );
  return (
    <div className="bp-find">
      <svg className="bp-find-ic" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6" />
        <path d="M15 15l5 5" />
      </svg>
      <input
        ref={input}
        type="search"
        role="combobox"
        // a name of its own: the customer list's "Find a customer" box stays the only one called so
        aria-label="Search everything"
        aria-keyshortcuts="/"
        aria-autocomplete="list"
        aria-expanded={listed}
        aria-controls={`${id}-list`}
        {...(listed && at >= 0 ? { 'aria-activedescendant': optionId(at) } : {})}
        placeholder="Find a customer, job, invoice or phone"
        autoComplete="off"
        spellCheck={false}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          close();
          if (!input.current?.value) setShown(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
            setAt((i) => step(i, hits.length, e.key === 'ArrowDown' ? 1 : -1));
          } else if (e.key === 'Enter') {
            const h = hits[at] ?? hits[0];
            if (listed && h) {
              e.preventDefault();
              go(h);
            }
          } else if (e.key === 'Escape') {
            e.preventDefault();
            if (listed || text) {
              setText('');
              close();
            } else {
              // an empty box goes back to the glass, the focus with it
              setShown(false);
              requestAnimationFrame(() => glass.current?.focus());
            }
          }
        }}
      />
      {!text && (
        <kbd className="bp-find-key" aria-hidden="true">
          /
        </kbd>
      )}
      {/* a press on an answer keeps the box's focus, so the list is still there to open it */}
      <div className="bp-find-pop" hidden={!listed} onMouseDown={(e) => e.preventDefault()}>
        <p className="bp-find-say" role="status">
          {state.kind === 'asking'
            ? 'Looking…'
            : state.kind === 'failed'
              ? 'The search didn’t answer. Try again.'
              : state.kind === 'found' && !hits.length
                ? `Nothing found for “${state.query}”.`
                : ''}
        </p>
        <div id={`${id}-list`} role="listbox" aria-label="What was found">
          {groups.map((g) => (
            <div key={g.name} role="group" aria-label={g.name} className="bp-find-grp">
              <div className="bp-find-grp-name" aria-hidden="true">
                {g.name}
              </div>
              {g.hits.map((h) => {
                const i = ++n;
                return (
                  <div
                    key={h.key}
                    id={optionId(i)}
                    role="option"
                    aria-selected={i === at}
                    className="bp-find-hit"
                    onMouseEnter={() => setAt(i)}
                    onClick={() => go(h)}
                  >
                    <span className="bp-find-title">{h.title}</span>
                    <span className="bp-find-detail">{h.detail}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
